import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgForm } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../auth.service';
import { HeaderService } from '../../shared/header/header.service';
import { FooterService } from '../../shared/footer/footer.service';
import { NotificationMessageService } from '../../shared/notification-message/notification-message.service';
import { NavigationService } from '../../shared/services/navigation.service';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';

@Component({
	selector: 'app-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit, OnDestroy {

	showCaptcha:boolean = false;
	blockRemining: number;
	disableStandardLogin: boolean = false;
	isSsoLoginInProgress: boolean = false;
	showPopupHelp: boolean = false;
	loginBlock: any ={
		recaptcha: ''
	};

	// Subject for unsubscribing from observables
	private readonly _destroying$ = new Subject<void>();

	@ViewChild("captcha") captcha;

	constructor(
		private authService: AuthService,
		private header: HeaderService,
		private footer: FooterService,
		private router: Router,
		private notificationMessage: NotificationMessageService,
		private navigation: NavigationService,
		private translateService: TranslateService,
		private httpClient: HttpClient,
		private msalService: MsalService
	) {
		this.header.hide();
		this.footer.hide();
	}

	isAct = true;
	yearAge = new Date().getFullYear();

	ngOnInit() {
		// Check if we need to disable the standard login button
		// This happens when a user tries to login from a different IP address
		this.authService.checkLoginStatus().subscribe((res: any) => {
			if (res && res.disableStandardLogin) {
				this.disableStandardLogin = true;
				// Show a notification message explaining why the standard login button is disabled
				this.notificationMessage.setMessage(this.translateService.instant('user.login_from_different_ip_use_sso'));
			}
		});

		// For a brand new system with a brand new browser and session,
		// we want to ensure the default state is "SSO Login"
		// This is the most important part - we always start with false
		this.isSsoLoginInProgress = false;

		// Log the initial interaction status for debugging
		console.log('Initial SSO login status:', {
			isSsoLoginInProgress: this.isSsoLoginInProgress,
			fromAuthService: this.authService.isInteractionInProgress()
		});

		// Check for SSO callback errors in the URL
		this.handleSsoCallbackErrors();

		// Check if we're returning from a redirect-based authentication
		this.handleRedirectResponse();

		// Subscribe to changes in the interaction status
		// But only update after the initial page load
		setTimeout(() => {
			this.authService.getInteractionStatus()
				.pipe(takeUntil(this._destroying$))
				.subscribe((inProgress: boolean) => {
					// Log the interaction status change for debugging
					console.log('SSO login status changed:', {
						previous: this.isSsoLoginInProgress,
						current: inProgress
					});

					this.isSsoLoginInProgress = inProgress;
				});
		}, 100); // Small delay to ensure the default state is shown first
	}

	/**
	 * Handles SSO callback errors from the URL parameters
	 * This is called when the user is redirected back from Microsoft with an error
	 */
	private handleSsoCallbackErrors() {
		// First check for errors stored in localStorage from app component
		const storedErrorJson = localStorage.getItem('msal.redirect.error');
		if (storedErrorJson) {
			try {
				const storedError = JSON.parse(storedErrorJson);
				console.error('[SSO Redirect Error] Error from MSAL redirect handler', {
					errorCode: storedError.errorCode,
					errorMessage: storedError.errorMessage,
					timestamp: new Date().toISOString()
				});

				// Clear the stored error
				localStorage.removeItem('msal.redirect.error');

				// Handle specific error types
				if (storedError.errorCode === 'consent_required') {
					this.notificationMessage.setMessage(
						'❌ You declined the login consent. Please try again and click "Accept" when prompted.',
						'error',
						5000
					);
				} else if (storedError.errorCode === 'access_denied') {
					this.notificationMessage.setMessage(
						'❌ Access was denied. Please try again.',
						'error',
						5000
					);
				} else if (storedError.errorCode === 'user_cancelled') {
					this.notificationMessage.setMessage(
						'❌ You cancelled the login. Please try again.',
						'error',
						5000
					);
				} else if (storedError.errorCode === 'missing_code') {
					this.notificationMessage.setMessage(
						'❌ Authentication failed: Missing authorization code. Please try again.',
						'error',
						5000
					);
				} else {
					this.notificationMessage.setMessage(
						`❌ Authentication failed: ${storedError.errorMessage || storedError.errorCode}. Please try again.`,
						'error',
						5000
					);
				}
				return;
			} catch (e) {
				console.error('Failed to parse stored MSAL error:', e);
				localStorage.removeItem('msal.redirect.error');
			}
		}

		// Then check for errors in URL parameters (from backend callback)
		const urlParams = new URLSearchParams(window.location.search);
		const error = urlParams.get('error');
		const errorDescription = urlParams.get('errorDescription');

		if (error) {
			console.error('[SSO Callback Error] Error returned from Microsoft', {
				error: error,
				errorDescription: errorDescription,
				timestamp: new Date().toISOString()
			});

			// Clear the URL parameters
			window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);

			// Handle specific error types
			if (error === 'consent_required') {
				this.notificationMessage.setMessage(
					'❌ You declined the login consent. Please try again and click "Accept" when prompted.',
					'error',
					5000
				);
			} else if (error === 'access_denied') {
				this.notificationMessage.setMessage(
					'❌ Access was denied. Please try again.',
					'error',
					5000
				);
			} else if (error === 'missing_code') {
				this.notificationMessage.setMessage(
					'❌ Authentication failed: Missing authorization code. Please try again.',
					'error',
					5000
				);
			} else {
				this.notificationMessage.setMessage(
					`❌ Authentication failed: ${errorDescription || error}. Please try again.`,
					'error',
					5000
				);
			}
		}
	}

	/**
	 * Handles the response from redirect-based authentication
	 * This is called when the component initializes to check if we're returning from a redirect
	 */
	private handleRedirectResponse() {
		// Check if we're returning from a redirect-based authentication
		const usingRedirect = localStorage.getItem('msal.using.redirect.auth');
		const redirectTimestamp = localStorage.getItem('msal.redirect.timestamp');

		if (usingRedirect === 'true' && redirectTimestamp) {
			// Check if the redirect happened recently (within the last 5 minutes)
			const timestamp = parseInt(redirectTimestamp, 10);
			const now = Date.now();
			const fiveMinutesInMs = 5 * 60 * 1000;

			if (now - timestamp < fiveMinutesInMs) {
				console.log('🔍 Detected return from redirect-based authentication');

				// Show a loading message
				this.notificationMessage.setMessage('Completing SSO login...', 'info', 3000);

				// Handle the redirect response
				this.msalService.handleRedirectObservable().subscribe(
					(response) => {
						console.log('✅ Successfully handled redirect response:', response);

						if (response && response.accessToken) {
							// Check if app component is already handling the authentication
							const appHandlingAuth = localStorage.getItem('msal.app.handling.auth');
							if (appHandlingAuth === 'true') {
								console.log('🔄 App component is handling authentication, skipping duplicate backend call');
								// Clear the redirect flags and let app component handle the rest
								localStorage.removeItem('msal.using.redirect.auth');
								localStorage.removeItem('msal.redirect.timestamp');
								return;
							}

							// MSAL login successful - now verify account exists in backend
							console.log('🚀 MSAL authentication successful, verifying account in backend');

							// Make backend call to verify account exists and create session
							this.httpClient.post(window.location.origin + '/msalAuth/login', {
								accessToken: response.accessToken,
								account: response.account
							}).subscribe(
								(res: any) => {
									console.log('Backend login call succeeded:', res);

									// Check if account verification failed
									if (res && res.code === -3) {
										// Account not found - store SSO data and redirect to registration
										if (res.ssoData) {
											// Store the SSO data in the auth service for the registration component
											this.authService.setSsoRegistrationData(res.ssoData);

											// Show message and redirect to registration
											this.notificationMessage.setMessage(
												this.translateService.instant('user.sso_login_no_account'),
												'info',
												3000
											);

											// Navigate to registration page with SSO data pre-populated
											setTimeout(() => {
												this.router.navigate(['/register']);
											}, 500);
										} else {
											// Fallback if no SSO data (shouldn't happen)
											this.notificationMessage.setMessage(
												this.translateService.instant('user.sso_login_no_account')
											);
										}
										return;
									}

									// Account verified successfully - update user ID and redirect to sites-map for SSO login
									this.authService.updateUserIdAfterLogin();
									this.notificationMessage.setMessage('SSO Login successful! Redirecting to sites map...', 'success', 2000);

									// Clear authentication parameters from URL and redirect to sites-map
									console.log('🔄 Clearing URL parameters and redirecting to sites-map');
									window.history.replaceState({}, document.title, window.location.pathname + '#/sites-map');
									this.router.navigate(['/sites-map']);
								},
								error => {
									console.error('❌ Backend login call failed:', error);
									this.notificationMessage.setMessage(this.translateService.instant('user.sso_login_failed'));
									// Clear URL parameters on error
									window.history.replaceState({}, document.title, window.location.pathname + '#/login');
								}
							);
						} else {
							console.log('⚠️ No access token in MSAL response, clearing URL parameters');
							// Clear URL parameters if no access token
							window.history.replaceState({}, document.title, window.location.pathname + '#/login');
						}

						// Clear the redirect flags
						localStorage.removeItem('msal.using.redirect.auth');
						localStorage.removeItem('msal.redirect.timestamp');
					},
					(error) => {
						console.error('❌ Error handling redirect response:', error);
						this.notificationMessage.setMessage(this.translateService.instant('user.sso_login_failed'));

						// Clear URL parameters and redirect flags on error
						window.history.replaceState({}, document.title, window.location.pathname + '#/login');
						localStorage.removeItem('msal.using.redirect.auth');
						localStorage.removeItem('msal.redirect.timestamp');
					}
				);
			} else {
				// The redirect happened too long ago, clear the flags
				localStorage.removeItem('msal.using.redirect.auth');
				localStorage.removeItem('msal.redirect.timestamp');
			}
		}
	}

	onLogin(form: NgForm) {
		const username = form.value.username;
		const password = form.value.password;
		const recaptcha = this.loginBlock.recaptcha;

		// First check if we need to disable the standard login button
		// This happens when a user tries to login from a different IP address
		this.authService.checkLoginStatus().subscribe((res: any) => {
			if (res && res.disableStandardLogin) {
				this.disableStandardLogin = true;
			}

			// Proceed with login attempt
			this.authService.loginUser({
				username: username,
				password: password,
				roleId: -1,
				name: '',
				recaptcha: recaptcha
			}).subscribe((res: any) => {
				// Check if we need to disable the standard login button based on the response
				if (res['obj'] && res['obj'].disableStandardLogin) {
					this.disableStandardLogin = true;
					// Show a notification message explaining why the standard login button is disabled
					this.notificationMessage.setMessage(this.translateService.instant('user.login_from_different_ip_use_sso'));
				}

				if (res['obj'].code == 1) {
					// SUCCESSFUL LOGIN
					let url = this.navigation.getPreviousUrl();
					if (url) {
						this.router.navigateByUrl(url);
					} else {
						this.router.navigate(['/sites-map']);
					}
				} else if (res['obj']?.showCaptcha){
					this.notificationMessage.setMessage(this.translateService.instant('user.too_many_invalid_attempt_login'))
					this.showCaptcha = res['obj'].showCaptcha;
					this.resetCaptcha();
				} else if (res['obj']?.shouldBlockLogin){
					this.showCaptcha = true;
					this.blockRemining = res['obj'].blockTimeRemaining;
					this.resetCaptcha();
					this.notificationMessage.setMessage(this.translateService.instant('user.login_blocked_try_after_x_mints')+ (` ${this.blockRemining} minutes`));
				} else if (res['obj'].code == -1) {
					this.notificationMessage.setMessage(this.translateService.instant('user.invalid_username_or_password'));
				} else if (res['obj'].code == -2) {
					this.notificationMessage.setMessage(this.translateService.instant('user.login_to_actview_is_not_allowed'));
				} else {
					this.notificationMessage.setMessage('globalErrMsg');
				}
			});
		});
	}

	resetCaptcha() {
		if (this.captcha) {
			this.captcha.reset();
			this.loginBlock.recaptcha = '';
		}
	  }

	/**
	 * Handles the SSO Login button click
	 * Uses MSAL for authentication
	 */
	onMsalLogin() {
		// If SSO login is already in progress, show a message and return
		if (this.isSsoLoginInProgress) {
			console.warn('SSO login already in progress, showing notification');
			this.notificationMessage.setMessage(this.translateService.instant('user.sso_login_in_progress'));
			return;
		}

		// Show a loading message to indicate the process has started
		this.notificationMessage.setMessage('Opening Microsoft login popup...', 'info', 5000);

		console.log('[SSO Login] User clicked SSO login button', {
			timestamp: new Date().toISOString()
		});

		try {

			// Call the loginWithMsal method, which now uses loginRedirect
			// This will redirect the browser to Microsoft's login page
			this.authService.loginWithMsal().subscribe(
				(res: any) => {

					console.log('[SSO Login] Received response from loginWithMsal', {
						responseType: res?.type,
						hasAccessToken: !!res?.accessToken,
						timestamp: new Date().toISOString()
					});

					// Handle popup blocker detection
					if (res?.type === 'popup_blocked') {

						this.notificationMessage.setMessage(
							'⚠️ Popup blocked by browser. Please allow popups for this site and try again, or use redirect authentication.',
							'warning',
							5000
						);
						return;
					}

					// ENHANCED: Handle automatic redirect fallback (no user interaction needed)
					if (res?.type === 'auto_redirect_fallback') {

						this.notificationMessage.setMessage(
							'Switching to redirect-based authentication...',
							'info',
							2000
						);
						// No user interaction needed - redirect is already happening
						return;
					}

					// Handle popup error fallback
					if (res?.type === 'popup_error_fallback') {


						// Show user a choice to retry with redirect
						const userConfirmed = confirm(
							'Popup authentication failed. Would you like to try redirect-based authentication instead?\n\n' +
							'Click OK to use redirect authentication, or Cancel to try again.'
						);

						if (userConfirmed) {

							this.notificationMessage.setMessage(
								'Switching to redirect-based authentication...',
								'info',
								2000
							);

							// Use redirect fallback
							setTimeout(() => {
								this.authService.loginWithRedirectFallback();
							}, 500);
						} else {

							this.isSsoLoginInProgress = false;
							this.notificationMessage.setMessage(
								'Please try again or check your popup blocker settings.',
								'warning',
								5000
							);
						}
						return;
					}

					// Handle interaction stuck error
					if (res?.type === 'interaction_stuck') {


						// Show user multiple options to resolve the stuck interaction
						const userChoice = confirm(
							'Authentication appears to be stuck. Choose an option:\n\n' +
							'OK = Force Reset (clears all authentication data)\n' +
							'Cancel = Try Redirect Authentication'
						);

						if (userChoice) {

							this.notificationMessage.setMessage(
								'Resetting authentication state...',
								'warning',
								3000
							);

							try {
								this.authService.forceResetAuthentication();
								this.isSsoLoginInProgress = false;
								this.notificationMessage.setMessage(
									'Authentication reset complete. Please try logging in again.',
									'success',
									5000
								);
							} catch (error) {
								console.error('Force reset failed:', error);
								this.notificationMessage.setMessage(
									'Reset failed. Please refresh the page and try again.',
									'error',
									5000
								);
							}
						} else {

							this.notificationMessage.setMessage(
								'Switching to redirect-based authentication...',
								'info',
								2000
							);

							// Use redirect fallback
							setTimeout(() => {
								this.authService.loginWithRedirectFallback();
							}, 500);
						}
						return;
					}

					// Check for MSAL success indicators
					if (res && (res.accessToken || res.account)) {
						// SUCCESSFUL MSAL LOGIN - update user ID and redirect to sites-map
						this.authService.updateUserIdAfterLogin();
						this.notificationMessage.setMessage('SSO Login successful! Redirecting to sites map...', 'success', 2000);

						// Clear any authentication parameters from URL and redirect to sites-map
						console.log('🔄 Popup SSO login successful, redirecting to sites-map');
						window.history.replaceState({}, document.title, window.location.pathname + '#/sites-map');
						this.router.navigate(['/sites-map']);
					} else if (res && res.type === 'popup_blocked') {
						// POPUP BLOCKED - we're switching to redirect-based authentication
						this.notificationMessage.setMessage(res.message || 'Switching to redirect-based authentication...', 'info', 5000);
						// The redirect will happen automatically from the auth service
					} else if (res && res.redirecting) {
						// Redirecting to Microsoft login page
					} else if (res && res.obj && res.obj.code === 1) {
						// LEGACY SUCCESSFUL LOGIN (for backwards compatibility)
						this.notificationMessage.setMessage('Login successful! Redirecting...', 'success', 2000);
						let url = this.navigation.getPreviousUrl();
						if (url) {
							this.router.navigateByUrl(url);
						} else {
							this.router.navigate(['/']);
						}
					} else if (res && res.code === -3) {
						// SSO ACCOUNT NOT FOUND - redirect to registration page
						if (res.ssoData) {
							// Store the SSO data in the auth service for the registration component
							this.authService.setSsoRegistrationData(res.ssoData);

							// Show message and redirect to registration
							this.notificationMessage.setMessage(
								this.translateService.instant('user.sso_login_no_account'),
								'info',
								3000
							);

							// Navigate to registration page with SSO data pre-populated
							setTimeout(() => {
								this.router.navigate(['/register']);
							}, 500);
						} else {
							// Fallback if no SSO data (shouldn't happen)
							this.notificationMessage.setMessage(
								this.translateService.instant('user.sso_login_no_account')
							);
						}
					} else {
						// Only log as error if we don't have any success indicators
						console.warn('Unexpected response format (not necessarily an error):', res);
						// Don't automatically show error message - let the user try again
						// this.notificationMessage.setMessage(this.translateService.instant('user.sso_login_failed'));
					}
				},
				(error) => {
					console.error('[SSO Login] MSAL login error in component', {
						errorCode: error?.errorCode,
						errorMessage: error?.message,
						timestamp: new Date().toISOString()
					});

					// Check if this is an "interaction_in_progress" error
					if (error?.message?.includes('interaction_in_progress') || error?.errorCode === 'interaction_in_progress') {
						console.warn('[SSO Login] Interaction in progress error detected in component');
						// Show a user-friendly message
						this.notificationMessage.setMessage(this.translateService.instant('user.sso_login_in_progress'));
					} else if (error?.message?.includes('consent_required') || error?.errorCode === 'consent_required') {
						console.warn('[SSO Login] Consent required error - user may have declined consent');
						this.notificationMessage.setMessage(
							'❌ You declined the login consent. Please try again and click "Accept" when prompted.',
							'error',
							5000
						);
					} else {
						// Provide specific error messages based on the error type
						let errorMessage = this.translateService.instant('user.sso_login_failed');
						if (error?.message?.includes('Popup blocker') || error?.message?.includes('popup_window_error')) {
							console.warn('[SSO Login] Popup blocker detected');
							errorMessage = '⚠️ Popup blocked by browser. Please allow popups for this site and try again.';
						} else if (error?.message?.includes('user_cancelled')) {
							console.warn('[SSO Login] User cancelled login');
							errorMessage = 'Login was cancelled. Please try again.';
						} else if (error?.message?.includes('timeout')) {
							console.warn('[SSO Login] Login timeout');
							errorMessage = 'Login timed out. Please try again.';
						}

						this.notificationMessage.setMessage(errorMessage);
					}
				}
			);
		} catch (error) {
			// Handle any unexpected errors
			console.error('Unexpected error during MSAL login:', error);
			this.notificationMessage.setMessage(this.translateService.instant('user.sso_login_failed'));
		}
	}

	/**
	 * Clears the SSO login status
	 * This is called when the user clicks the SSO login button while an interaction is in progress
	 */
	clearSsoLoginStatus() {
		console.log('Clearing SSO login status');

		// Clear the interaction status in the AuthService
		this.authService.clearInteractionStatus();

		// Update the local property
		this.isSsoLoginInProgress = false;

		// Show a notification message
		this.notificationMessage.setMessage(this.translateService.instant('user.sso_login_status_cleared'));
	}



	/**
	 * Tries redirect-based authentication as an alternative to popup
	 */
	tryRedirectAuth(): void {


		this.showPopupHelp = false;
		this.isSsoLoginInProgress = true;

		this.notificationMessage.setMessage(
			'Switching to redirect-based authentication...',
			'info',
			2000
		);

		// Use redirect fallback
		setTimeout(() => {
			this.authService.loginWithRedirectFallback();
		}, 500);
	}

	ngOnDestroy() {
		this.header.show();
		this.footer.show();

		// Unsubscribe from all observables
		this._destroying$.next();
		this._destroying$.complete();

		// Clean up AuthService subscriptions
		this.authService.cleanup();
	}
}
